import { useEffect, useState } from 'react'

/**
 * One big number with − and + either side — thumb-sized, and typeable for the
 * "22" that nobody wants to tap out with +.
 *
 * The text is held separately from the number so "2." can be typed on the way
 * to "2.5"; the number is only reported when the text parses.
 */
export function CountStepper({
  value,
  onChange,
  whole,
  min = 0,
}: {
  value: number
  onChange: (next: number) => void
  /** Whole units only — serialized parts. Bulk takes decimals (2.5 m). */
  whole: boolean
  min?: number
}): JSX.Element {
  const [text, setText] = useState(value === 0 ? '' : String(value))
  // Follow the value when it changes from outside (a scan raised the count).
  useEffect(() => {
    if (Number(text) !== value) setText(value === 0 ? '' : String(value))
    // `text` is deliberately not a dependency: typing must not be overwritten.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const step = (delta: number): void => onChange(Math.max(min, Math.round((value + delta) * 1000) / 1000))

  return (
    <div className="inv-stepper">
      <button type="button" aria-label="One fewer" onClick={() => step(-1)} disabled={value - 1 < min}>
        −
      </button>
      <input
        type="text"
        inputMode={whole ? 'numeric' : 'decimal'}
        aria-label="How many"
        className="inv-stepper__value"
        value={text}
        placeholder="0"
        onChange={(e) => {
          const raw = e.target.value.replace(',', '.').replace(whole ? /[^0-9]/g : /[^0-9.]/g, '')
          setText(raw)
          const n = Number(raw)
          if (raw !== '' && Number.isFinite(n)) onChange(n)
          else if (raw === '') onChange(0)
        }}
        onFocus={(e) => e.target.select()}
      />
      <button type="button" aria-label="One more" onClick={() => step(1)}>
        +
      </button>
    </div>
  )
}
